import React from "react";
import parse from "html-react-parser";
import Script from "next/script";
import getConfig from "next/config";
import Head from "next/head";

import { AMP_EXCLUDED_ARTICLES } from "utils/constants";
import { ArticleLeadMedia, ArticlePrimaryTopic } from "utils/orm";

interface Breadcrumb {
    uri: string;
    title: string;
}

interface SEOProps {
    articleUri: string;
    canonical: string;
    breadcrumbLinks: Breadcrumb[];
    dateUpdated: string;
    description: string;
    leadMedia: ArticleLeadMedia;
    postDate: string;
    primaryTopic: ArticlePrimaryTopic;
    title: string;
    writerName: string;
    writerUri: string;
    seo: { MetaTitleContainer: string, MetaTagContainer: string }
}

interface OpenGraphProps {
    author: string;
    canonical: string;
    description: string;
    leadMediaAlt: string;
    leadMediaHeight: number;
    leadMediaUrl: string;
    leadMediaWidth: number;
    modifiedTime: string;
    postDate: string;
    title: string;
}

/**
 * Generates the open graph object.
 * @returns an Object
 */
const genOpenGraphJSON = (props: OpenGraphProps) => {
    const {
        author,
        canonical,
        description,
        leadMediaAlt,
        leadMediaHeight,
        leadMediaUrl,
        leadMediaWidth,
        modifiedTime,
        postDate,
        title,
    } = props;
    return {
        title,
        description,
        url: canonical,
        type: "article",
        site_name: "The Muse",
        locale: "en_US",
        article: {
            publishedTime: postDate,
            modifiedTime,
            authors: [author]
        },
        images: [{
            alt: leadMediaAlt,
            url: leadMediaUrl,
            width: leadMediaWidth,
            height: leadMediaHeight
        }]
    };
};

/**
 * Generates the Twitter object passed into NextSEO.
 * @returns an Object
 */
const genTwitterJSON = () => ({
    handle: "@TheMuse",
    site: "@TheMuse",
    cardType: "summary_large_image",
});

interface ArticleJSONProps {
    canonical: string,
    description: string,
    leadMediaUrl: string,
    modifiedTime: string,
    postDate: string,
    title: string,
    writerName: string,
    author: string,
}

/**
 * Generates the article schema object passed into LD+Json.
 * @returns an Object
 */
const genArticleJSON = (props: ArticleJSONProps) => {
    const {
        canonical,
        description,
        leadMediaUrl,
        modifiedTime,
        postDate,
        title,
        writerName,
        author,
    } = props;
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        datePublished: postDate,
        description,
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${canonical}`
        },
        headline: title,
        image: {
            "@type": "ImageObject",
            url: leadMediaUrl,
            width: 820,
            height: 430
        },
        dateModified: modifiedTime,
        author: [
            {
                "@type": "Person",
                name: writerName,
                url: author
            }
        ],
        publisher: {
            "@type": "Organization",
            name: "The Muse",
            logo: {
                "@type": "ImageObject",
                url: "https://assets.themuse.com/uploaded/companies/1327/small_logo.png?v=1664489768",
                width: 290,
                height: 60
            },
            url: "https://www.themuse.com",
            sameAs: [
                "https://www.facebook.com/thedailymuse",
                "https://twitter.com/TheMuse",
                "https://www.instagram.com/themuse/",
                "https://www.youtube.com/channel/UCk4bbQAZD26f_XdGyb4wwhg",
                "https://www.linkedin.com/company/the-daily-muse/",
                "https://en.wikipedia.org/wiki/The_Muse_(website)",
                "https://www.pinterest.com/thedailymuse/_created/"
            ],
        }
    };
};

/**
 * Generates the breadcrumb schema object passed into LD+Json.
 * @returns an Object
 */
const genBreadcrumbJSON = (breadcrumbLinks: Breadcrumb[]) => {
    const cleanCrumbs = breadcrumbLinks.filter((x: Breadcrumb) => x.title && x.uri);
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: cleanCrumbs.map((x: Breadcrumb, i: number) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@id": `https://www.themuse.com/advice/${x.uri}`,
                name: (/^advice$/i).test(x.title) ? "Advice" : x.title
            }
        }))
    };
};

/**
 * Generates the organization schema object passed into LD+Json.
 * @returns an Object
 */
const genOrganizationJSON = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    logo: "https://assets.themuse.com/uploaded/companies/1327/small_logo.png?v=1664489768",
    legalName: "Daily Muse Inc.",
    name: "The Muse",
    sameAs: [
        "https://www.facebook.com/thedailymuse",
        "https://twitter.com/TheMuse",
        "https://www.instagram.com/themuse/",
        "https://www.youtube.com/channel/UCk4bbQAZD26f_XdGyb4wwhg",
        "https://www.linkedin.com/company/the-daily-muse/",
        "https://en.wikipedia.org/wiki/The_Muse_(website)",
        "https://www.pinterest.com/thedailymuse/_created/"
    ],
    url: "https://www.themuse.com/",
    address: {
        "@type": "PostalAddress",
        streetAddress: "1375 Broadway, 20th Floor",
        addressLocality: "New York",
        addressRegion: "NY",
        postalCode: 10018,
        addressCountry: "US"
    }
});

/**
 * Generates the SEO data for the head both LD+JSON and MetaData links.
 * @returns React.ReactElement
 */
const SEO = (props: SEOProps): React.ReactElement => {
    const { publicRuntimeConfig: { museHost } } = getConfig();
    const {
        articleUri,
        canonical,
        breadcrumbLinks,
        dateUpdated,
        description,
        leadMedia,
        postDate,
        primaryTopic,
        seo,
        title,
        writerName,
        writerUri,
    } = props;
    const {
        altText,
        url,
    } = leadMedia || {};
    const modifiedTime = postDate > dateUpdated ? postDate : dateUpdated;
    const author = `${museHost}/${writerUri}`;
    const includeAmp = !AMP_EXCLUDED_ARTICLES.includes(articleUri);

    return (
        <>
            <Head>
                <link rel="canonical" href={canonical} key="canonical" />
                <meta name="robots" content="index,follow" key="robots" />
                {parse(seo.MetaTitleContainer)}
                {parse(seo.MetaTagContainer)}
                {includeAmp ? <link rel="amphtml" href={`${museHost}/amp/${articleUri}`} /> : null}
                <meta property="og:image:alt" content={altText} />
                <meta property="article:author" content={author} />
                <meta property="article:modified_time" content={modifiedTime} />
                <meta property="article:published_time" content={postDate} />
                <meta property="article:section" content="Advice" />
                <meta property="article:tag" content={primaryTopic.title} />
            </Head>
            <Script
                strategy="beforeInteractive"
                id="Article-JSON"
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(genArticleJSON({
                        canonical,
                        description,
                        leadMediaUrl: url,
                        modifiedTime,
                        postDate,
                        title,
                        writerName,
                        author,
                    }))
                }}
            />
            <Script
                strategy="beforeInteractive"
                id="BreadcrumbList-JSON"
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(genBreadcrumbJSON(breadcrumbLinks))
                }}
            />
            <Script
                strategy="beforeInteractive"
                id="Organization-JSON"
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(genOrganizationJSON())
                }}
            />
        </>
    );
};

// WARNING: These are for unit testing only and should NEVER be used directly outside this file.
const openGraphJSONTestingOnly = genOpenGraphJSON;
const twitterJSONTestingOnly = genTwitterJSON;
const articleJSONTestingOnly = genArticleJSON;
const breadcrumbJSONTestingOnly = genBreadcrumbJSON;
const organizationJSONTestingOnly = genOrganizationJSON;

export {
    SEO,
    openGraphJSONTestingOnly,
    twitterJSONTestingOnly,
    articleJSONTestingOnly,
    breadcrumbJSONTestingOnly,
    organizationJSONTestingOnly
};
